import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Shield, UserPlus, ArrowLeft, Trash2, Edit, Users, Loader2, Mail, UserCog } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CompanySearch from '@/components/CompanySearch';

const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [userTab, setUserTab] = useState('admins');

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    company_name: '',
    website_url: '',
    is_support_client: false,
    contract_hours: 0,
    contract_amount: 0,
    clickup_list_id: null,
    clickup_space_id: null
  });

  // Edit State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    company_name: '',
    website_url: '',
    company_id: null,
    is_support_client: false,
    contract_hours: 0,
    contract_amount: 0,
    clickup_list_id: '',
    role: 'user'
  });

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, getAuthHeaders());
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies`, getAuthHeaders());
      setCompanies(res.data);
    } catch (e) { }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/register`, newUser, getAuthHeaders());
      toast.success('User created successfully!');
      setNewUser({
        name: '',
        email: '',
        password: '',
        role: 'user',
        company_name: '',
        website_url: '',
        is_support_client: false,
        contract_hours: 0,
        contract_amount: 0,
        clickup_list_id: null,
        clickup_space_id: null
      });
      setCreateDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}?role=${newRole}`, {}, getAuthHeaders());
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, getAuthHeaders());
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleSendPasswordReset = async userItem => {
    if (!window.confirm(`Send a password reset email to ${userItem.email}?`)) return;
    try {
      const response = await axios.post(`${API}/admin/users/${userItem.id}/send-password-reset`, {}, getAuthHeaders());
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send password reset email');
    }
  };

  const handleEditClick = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditForm({
      name: userToEdit.name || '',
      email: userToEdit.email,
      role: userToEdit.role || 'user',
      company_name: userToEdit.company_name || '',
      website_url: userToEdit.website_url || '',
      company_id: userToEdit.company_id || null,
      is_support_client: userToEdit.is_support_client,
      contract_hours: userToEdit.contract_hours || 0,
      contract_amount: userToEdit.contract_amount || 0,
      clickup_list_id: userToEdit.clickup_list_id || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateUserSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.patch(`${API}/admin/users/${editingUser.id}`, editForm, getAuthHeaders());
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-700',
      subadmin: 'bg-blue-100 text-blue-700',
      user: 'bg-gray-100 text-gray-700'
    };

    return (
      <Badge className={colors[role]}>
        {role}
      </Badge>
    );
  };

  const filteredUsers = users.filter(userItem => (
    userTab === 'admins'
      ? userItem.role === 'admin' || userItem.role === 'subadmin'
      : userItem.role === 'user'
  ));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-xs text-gray-500">User Management</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Button
                data-testid="back-to-dashboard-button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
            <p className="text-sm text-gray-600 mt-1">
              {users.length} {users.length === 1 ? 'user' : 'users'} registered
            </p>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-button" className="bg-black hover:bg-gray-800 flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-user-dialog" className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    data-testid="new-user-name-input"
                    placeholder="John Doe"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="new-user-email-input"
                    placeholder="user@example.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="new-user-password-input"
                    placeholder="••••••••"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    required
                  />
                </div>

                {!newUser.company_id && (
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name (Text)</Label>
                    <Input
                      id="company_name"
                      data-testid="new-user-company-input"
                      placeholder="e.g. Acme Corp"
                      value={newUser.company_name}
                      onChange={(e) => setNewUser({ ...newUser, company_name: e.target.value })}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Linked Company Profile</Label>
                  <CompanySearch
                    value={newUser.company_id || null}
                    onChange={(id) => setNewUser({ ...newUser, company_id: id || null })}
                    companies={companies}
                    placeholder="Search or create company..."
                  />
                </div>

                {!newUser.company_id && (
                  <div className="space-y-2">
                    <Label htmlFor="website_url">Website URL</Label>
                    <Input
                      id="website_url"
                      data-testid="new-user-website-input"
                      placeholder="https://example.com"
                      value={newUser.website_url}
                      onChange={(e) => setNewUser({ ...newUser, website_url: e.target.value })}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                  >
                    <SelectTrigger data-testid="new-user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="subadmin">Subadmin</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_support_client"
                    data-testid="new-user-support-client-checkbox"
                    checked={newUser.is_support_client}
                    onChange={(e) => setNewUser({ ...newUser, is_support_client: e.target.checked })}
                    className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                  />
                  <Label htmlFor="is_support_client" className="font-normal cursor-pointer">
                    Support Client (has contract hours)
                  </Label>
                </div>

                {newUser.is_support_client && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="contract_hours">Contract Hours / Month</Label>
                      <Input
                        id="contract_hours"
                        type="number"
                        step="0.5"
                        min="0"
                        data-testid="new-user-contract-hours-input"
                        placeholder="e.g., 20"
                        value={newUser.contract_hours}
                        onChange={(e) => setNewUser({ ...newUser, contract_hours: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contract_amount">Support Contract Value ($/month)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                        <Input
                          id="contract_amount"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="e.g., 500"
                          className="pl-7"
                          value={newUser.contract_amount}
                          onChange={(e) => setNewUser({ ...newUser, contract_amount: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="clickup_list_id">ClickUp List ID (Optional)</Label>
                      <Input
                        id="clickup_list_id"
                        placeholder="Paste ClickUp List ID here"
                        value={newUser.clickup_list_id || ''}
                        onChange={(e) => setNewUser({ ...newUser, clickup_list_id: e.target.value || null })}
                      />
                      <p className="text-xs text-gray-500">
                        Leave empty to use the default ClickUp list. To find a List ID: open the list in ClickUp and copy everything after <strong>/l/</strong> in the URL (e.g., from <code>https://app.clickup.com/12345/v/l/<strong>6-901702379808-1</strong></code>, copy <strong>6-901702379808-1</strong>).
                      </p>
                    </div>
                  </>
                )}

                <Button type="submit" data-testid="submit-user-button" className="w-full bg-black hover:bg-gray-800">
                  Create User
                </Button>
              </form>
            </DialogContent>
          </Dialog>


          {/* Edit User Dialog */}
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateUserSubmit} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_name">Full Name</Label>
                  <Input
                    id="edit_name"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_email">Email</Label>
                  <Input
                    id="edit_email"
                    type="email"
                    required
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_company">Company Name (Text)</Label>
                  <Input
                    id="edit_company"
                    value={editForm.company_name}
                    onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Linked Company Profile</Label>
                  <CompanySearch
                    value={editForm.company_id || null}
                    onChange={(id) => setEditForm({ ...editForm, company_id: id || null })}
                    companies={companies}
                    placeholder="Search or create company..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_website">Website URL</Label>
                  <Input
                    id="edit_website"
                    value={editForm.website_url}
                    onChange={(e) => setEditForm({ ...editForm, website_url: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="edit_support_client"
                      checked={editForm.is_support_client}
                      onChange={(e) => setEditForm({ ...editForm, is_support_client: e.target.checked })}
                      className="w-4 h-4 text-black border-gray-300 rounded focus:ring-black"
                    />
                    <Label htmlFor="edit_support_client" className="font-normal cursor-pointer">
                      Support Client (has contract hours)
                    </Label>
                  </div>
                </div>
                {editForm.is_support_client && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="edit_hours">Contract Hours / Month</Label>
                      <Input
                        id="edit_hours"
                        type="number"
                        step="0.5"
                        value={editForm.contract_hours}
                        onChange={(e) => setEditForm({ ...editForm, contract_hours: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_amount">Support Contract Value ($/month)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                        <Input
                          id="edit_amount"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="e.g., 500"
                          className="pl-7"
                          value={editForm.contract_amount}
                          onChange={(e) => setEditForm({ ...editForm, contract_amount: parseFloat(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit_clickup">ClickUp List ID</Label>
                      <Input
                        id="edit_clickup"
                        value={editForm.clickup_list_id}
                        onChange={(e) => setEditForm({ ...editForm, clickup_list_id: e.target.value })}
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label htmlFor="edit_role">Role</Label>
                  <Select
                    value={editForm.role}
                    onValueChange={(val) => setEditForm({ ...editForm, role: val })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="subadmin">Subadmin</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="w-full bg-black hover:bg-gray-800">
                  Update User
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div >

        <div className="flex gap-2 mb-5 border-b">
          <button onClick={() => setUserTab('admins')} className={`px-4 py-3 text-sm font-medium border-b-2 ${userTab === 'admins' ? 'border-black text-black' : 'border-transparent text-gray-500'}`}>
            <UserCog className="w-4 h-4 inline mr-2" />Admins ({users.filter(item => item.role !== 'user').length})
          </button>
          <button onClick={() => setUserTab('users')} className={`px-4 py-3 text-sm font-medium border-b-2 ${userTab === 'users' ? 'border-black text-black' : 'border-transparent text-gray-500'}`}>
            <Users className="w-4 h-4 inline mr-2" />Clients / Users ({users.filter(item => item.role === 'user').length})
          </button>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="grid gap-4" data-testid="users-list">
            {filteredUsers.map((userItem) => (
              <Card
                key={userItem.id}
                data-testid={`user-card-${userItem.id}`}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/admin/users/${userItem.id}`)}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        {userItem.name || userItem.company_name || userItem.email}
                      </CardTitle>
                      {(userItem.name || userItem.company_name) ? (
                        <p className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                          <Users className="w-4 h-4" /> {userItem.email}
                          {userItem.name && userItem.company_name && ` — ${userItem.company_name}`}
                        </p>
                      ) : null}

                      <div className="mt-4 space-y-1">
                        {userItem.website_url && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Website:</span>{' '}
                            <a
                              href={userItem.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-600 hover:underline"
                            >
                              {userItem.website_url}
                            </a>
                          </p>
                        )}
                        {userItem.is_support_client && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Contract Hours:</span> {(userItem.hours_used || 0).toFixed(1)} / {(userItem.contract_hours || 0).toFixed(1)}h used
                          </p>
                        )}
                        {userItem.is_support_client && userItem.contract_amount > 0 && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Contract Value:</span> ${(userItem.contract_amount || 0).toFixed(2)}/mo
                          </p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          Created: {new Date(userItem.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {userItem.is_support_client && (
                        <Badge className="bg-purple-100 text-purple-700">Support Client</Badge>
                      )}
                      {getRoleBadge(userItem.role)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Select
                        value={userItem.role}
                        onValueChange={(value) => handleUpdateRole(userItem.id, value)}
                        disabled={userItem.id === user.id}
                      >
                        <SelectTrigger data-testid={`role-select-${userItem.id}`} className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="z-50">
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="subadmin">Subadmin</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-2">
                      {userItem.id !== user.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendPasswordReset(userItem);
                          }}
                          className="text-violet-600 hover:text-violet-700 hover:bg-violet-50 border-violet-200"
                          title="Send password reset email"
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Reset
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(userItem);
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>

                      {userItem.id !== user.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              data-testid={`delete-user-button-${userItem.id}`}
                              variant="outline"
                              size="sm"
                              onClick={(e) => e.stopPropagation()}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete User</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete {userItem.email}? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                data-testid={`confirm-delete-${userItem.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteUser(userItem.id);
                                }}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
